/*  ProductEnquiryForm — reusable lead form on product marketing pages.
    ────────────────────────────────────────────────────────────────
    iter100.122 — Ships alongside the Guards & Retainer mega-menu.
    Each product page (Night Guard / Anti-Snoring / Sports Guard /
    Retainer) mounts this form. On submit we POST to
    `/api/product-enquiry` which writes to db.leads with source
    `product-<slug>` so the enquiry flows straight into the existing
    Leads Hub.

    Design principles per user's iter100.122 spec:
      • NO shopping cart, NO "Shop Now" — enquiry only.
      • Warm, low-friction (5 fields max, only phone + consent required).
      • Confirmation state after submit so the user sees success without
        leaving the page.
*/
import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle, PaperPlaneRight, ShieldCheck, Phone } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";


export default function ProductEnquiryForm({ productSlug, productTitle, ctaLabel, className = "" }) {
    const [form, setForm] = useState({ name: "", phone: "", city: "", notes: "", consent_given: false });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        const phone = form.phone.replace(/\D/g, "");
        if (phone.length < 6) {
            toast.error("Please enter a valid phone number.");
            return;
        }
        if (!form.consent_given) {
            toast.error("Please agree to be contacted.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/product-enquiry", {
                ...form,
                phone,
                product_slug: productSlug,
            });
            setDone(true);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div
                className={`bg-card border border-emerald-200 rounded-2xl p-6 text-center ${className}`}
                data-testid="product-enquiry-done"
            >
                <div className="w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-3">
                    <CheckCircle size={28} weight="fill" className="text-emerald-600" />
                </div>
                <div className="font-display text-xl font-bold text-foreground" data-testid="product-enquiry-done-title">
                    We&apos;ve got you.
                </div>
                <div className="text-sm text-muted-foreground mt-1.5 max-w-sm mx-auto">
                    A smile expert will call you shortly about {productTitle}. No commitment — just clarity.
                </div>
            </div>
        );
    }

    return (
        <form
            onSubmit={submit}
            className={`bg-card border border-border rounded-2xl p-5 sm:p-6 shadow-sm ${className}`}
            data-testid="product-enquiry-form"
        >
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary" data-testid="product-enquiry-eyebrow">
                Free consultation
            </div>
            <div className="font-display text-xl font-bold mt-0.5" data-testid="product-enquiry-title">
                Enquire about {productTitle}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
                Share your details — a certified smile expert will call you back with pricing, fitment steps, and next best action.
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
                <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name (optional)"
                    maxLength={80}
                    data-testid="product-enquiry-name"
                />
                <div className="relative">
                    <Phone size={14} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <Input
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="Mobile number *"
                        type="tel"
                        maxLength={20}
                        required
                        className="pl-8"
                        data-testid="product-enquiry-phone"
                    />
                </div>
            </div>

            <div className="mt-3">
                <Input
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder="City (helps us route you to a doctor near you)"
                    maxLength={60}
                    data-testid="product-enquiry-city"
                />
            </div>

            <div className="mt-3">
                <Textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Anything you'd like to share? (optional)"
                    rows={3}
                    maxLength={500}
                    data-testid="product-enquiry-notes"
                />
            </div>

            <label className="mt-3 flex items-start gap-2 text-[11.5px] text-muted-foreground cursor-pointer">
                <input
                    type="checkbox"
                    checked={form.consent_given}
                    onChange={(e) => setForm({ ...form, consent_given: e.target.checked })}
                    className="mt-0.5"
                    data-testid="product-enquiry-consent"
                    required
                />
                <span>
                    I agree to be contacted about my enquiry. No spam, ever — opt out anytime.
                </span>
            </label>

            <Button
                type="submit"
                disabled={submitting}
                className="w-full mt-4 h-11 text-sm font-semibold"
                data-testid="product-enquiry-submit"
            >
                <PaperPlaneRight size={14} weight="fill" className="mr-2" />
                {submitting ? "Sending…" : (ctaLabel || `Enquire about ${productTitle}`)}
            </Button>

            <div className="mt-3 flex items-center gap-1.5 text-[10.5px] text-muted-foreground justify-center">
                <ShieldCheck size={11} weight="duotone" className="text-emerald-600" />
                <span>Free & confidential · No clinic visit needed</span>
            </div>
        </form>
    );
}
